import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatPill } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtDA, fmtDate, STATUT_FACTURE } from "@/lib/format";
import { downloadPdf } from "@/lib/files";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Phone, Mail, MapPin, FileText, FileDown } from "lucide-react";

export default function FournisseurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [debut, setDebut] = useState("");
  const [fin, setFin] = useState("");

  useEffect(() => {
    api.get(`/fournisseurs/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  const downloadReleve = () => {
    const params = [];
    if (debut) params.push(`date_debut=${debut}`);
    if (fin) params.push(`date_fin=${fin}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    downloadPdf(`/pdf/releve-fournisseur/${id}${qs}`, `releve-fourn-${data.raison_sociale}`);
  };

  if (data === null) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (data === false) return <p className="text-destructive">Fournisseur introuvable.</p>;

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2" onClick={() => navigate("/fournisseurs")} data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Retour
      </Button>
      <PageHeader title={data.raison_sociale} subtitle={data.nom_commercial} testid="fournisseur-detail"
        actions={
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1"><label className="text-xs text-muted-foreground">Du</label><Input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-9 w-40" data-testid="releve-fourn-debut" /></div>
            <div className="space-y-1"><label className="text-xs text-muted-foreground">Au</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} className="h-9 w-40" data-testid="releve-fourn-fin" /></div>
            <Button variant="outline" className="gap-2" onClick={downloadReleve} data-testid="releve-fournisseur-btn"><FileDown className="h-4 w-4" /> Relevé de compte (PDF)</Button>
          </div>
        } />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border bg-card p-5 space-y-2.5">
          <h3 className="font-heading font-bold mb-2">Coordonnées</h3>
          {data.telephone && <p className="text-sm flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {data.telephone}</p>}
          {data.email && <p className="text-sm flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {data.email}</p>}
          {data.adresse && <p className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {data.adresse}</p>}
          <div className="pt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            {data.rc && <span>RC: {data.rc}</span>}
            {data.nif && <span>NIF: {data.nif}</span>}
            {data.nis && <span>NIS: {data.nis}</span>}
            {data.conditions_paiement && <span>Cond.: {data.conditions_paiement}</span>}
          </div>
        </div>
        <StatPill label="Total facturé" value={fmtDA(data.totaux.ttc)} />
        <StatPill label="Reste à payer" value={fmtDA(data.totaux.reste)} className={data.totaux.reste > 0 ? "border-destructive/30" : ""} />
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Factures</h3>
        </div>
        {data.factures.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Aucune facture enregistrée.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>N° Facture</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead className="text-right">TTC</TableHead>
                  <TableHead className="text-right">Payé</TableHead>
                  <TableHead className="text-right">Reste</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.factures.map((f) => {
                  const st = STATUT_FACTURE[f.statut] || {};
                  return (
                    <TableRow key={f.id}>
                      <TableCell className="tabular text-sm font-medium">{f.numero_facture}</TableCell>
                      <TableCell className="tabular text-sm">{fmtDate(f.date_facture)}</TableCell>
                      <TableCell className="tabular text-sm">{fmtDate(f.echeance)}</TableCell>
                      <TableCell className="tabular text-right">{fmtDA(f.ttc)}</TableCell>
                      <TableCell className="tabular text-right text-success">{fmtDA(f.montant_paye)}</TableCell>
                      <TableCell className="tabular text-right font-semibold">{fmtDA(f.reste)}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-1 rounded-md ${st.cls}`}>{st.label}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
